import { Component, Input, Output, EventEmitter, ElementRef, ViewChild, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';

declare var window: any;

@Component({
  selector: 'app-intl-phone-input',
  template: `
    <input
      #phoneInput
      [type]="type"
      [placeholder]="placeholder"
      [disabled]="disabled"
      [maxLength]="maxLength"
      [class]="cssClass"
      [style.width]="width"
      pInputText
    />
  `,
  styles: [`
    :host {
      display: block;
    }
    input.full-width {
      width: 100% !important;
    }
  `]
})
export class IntlPhoneInputComponent implements AfterViewInit, OnChanges {
  @Input() type: string = 'text';
  @Input() selectedCountryISO: string = 'auto';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() maxLength: string = '20';
  @Input() cssClass: string = '';
  @Input() width: string = 'auto';
  @Input() initialCountry: string = '';
  @Input() nationalMode: boolean = true;
  @Input() separateDialCode: boolean = false;
  @Input() enableAutoPlaceholder: boolean = true;
  @Input() value: string = '';

  private readonly allCountries: string[] = [
    'ru', 'by', 'kz',
    'ad', 'ae', 'af', 'ag', 'ai', 'al', 'am', 'ao', 'ar', 'at',
    'au', 'aw', 'az', 'ba', 'bb', 'bd', 'be', 'bf', 'bg', 'bh',
    'bi', 'bj', 'br', 'bs', 'bt', 'bw', 'bz', 'ca', 'cd', 'cf',
    'cg', 'ch', 'ci', 'cl', 'cm', 'cn', 'co', 'cr', 'cu', 'cv',
    'cy', 'cz', 'de', 'dj', 'dk', 'dm', 'do', 'dz', 'ec', 'ee',
    'eg', 'er', 'es', 'et', 'fi', 'fj', 'fm', 'fr', 'ga', 'gb',
    'gd', 'ge', 'gh', 'gm', 'gn', 'gr', 'gt', 'gw', 'gy', 'hn',
    'hr', 'ht', 'hu', 'id', 'ie', 'il', 'in', 'iq', 'ir', 'is',
    'it', 'jm', 'jo', 'jp', 'ke', 'kg', 'kh', 'ki', 'km', 'kn',
    'kp', 'kr', 'kw', 'la', 'lb', 'lc', 'li', 'lk', 'lr', 'ls',
    'lt', 'lu', 'lv', 'ly', 'ma', 'mc', 'md', 'me', 'mg', 'mh',
    'mk', 'ml', 'mm', 'mn', 'mr', 'mt', 'mu', 'mv', 'mw', 'mx',
    'my', 'mz', 'na', 'ne', 'ng', 'ni', 'nl', 'no', 'np', 'nr',
    'nz', 'om', 'pa', 'pe', 'pg', 'ph', 'pk', 'pl', 'pt', 'pw',
    'py', 'qa', 'ro', 'rs', 'rw', 'sa', 'sb', 'sc', 'sd', 'se',
    'sg', 'sh', 'si', 'sk', 'sl', 'sm', 'sn', 'so', 'sr', 'ss',
    'st', 'sv', 'sy', 'sz', 'td', 'tg', 'th', 'tj', 'tl', 'tm',
    'tn', 'to', 'tr', 'tt', 'tv', 'tz', 'ua', 'ug', 'us', 'uy',
    'uz', 'va', 'vc', 've', 'vn', 'vu', 'ws', 'xk', 'ye', 'za',
    'zm', 'zw'
  ];

  @Output() valueChange = new EventEmitter<any>();
  @Output() countryChange = new EventEmitter<any>();
  @Output() intlTelInputObject = new EventEmitter<any>();
  @Output() hasError = new EventEmitter<boolean>();

  @ViewChild('phoneInput') phoneInput!: ElementRef;

  private itiInstance: any = null;
  private utilsScriptLoaded: boolean = false;

  ngAfterViewInit() {
    this.loadUtilsScript().then(() => {
      this.initTelInput();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['disabled'] && this.itiInstance) {
      if (changes['disabled'].currentValue) {
        this.itiInstance.setEnabled(false);
      } else {
        this.itiInstance.setEnabled(true);
      }
    }
  }

  private loadUtilsScript(): Promise<void> {
    return new Promise((resolve) => {
      if (this.utilsScriptLoaded) {
        resolve();
        return;
      }

      const existingScript = document.querySelector('script[data-intl-tel-utils]');
      if (existingScript) {
        this.utilsScriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = '/assets/utils.js';
      script.setAttribute('data-intl-tel-utils', 'true');
      script.onload = () => {
        this.utilsScriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        this.utilsScriptLoaded = true;
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  private initTelInput() {
    if (!this.phoneInput || !this.phoneInput.nativeElement) return;

    const windowObj: any = window;
    if (!windowObj.intlTelInput) return;

    const options: any = {
      initialCountry: this.initialCountry || this.selectedCountryISO,
      nationalMode: this.nationalMode,
      separateDialCode: this.separateDialCode,
      preferredCountries: ['ru','kz','by'],
      enableAutoPlaceholder: this.enableAutoPlaceholder,
      utilsScript: null,
      onlyCountries: this.allCountries
    };

    this.itiInstance = windowObj.intlTelInput(this.phoneInput.nativeElement, options);

    // Слушаем изменения
    this.phoneInput.nativeElement.addEventListener('countrychange', () => {
      const countryData = this.itiInstance.getSelectedCountryData();
      this.countryChange.emit(countryData);
    });

    // intl-tel-input emit-ит 'input' при вводе номера
    this.phoneInput.nativeElement.addEventListener('input', () => {
      const value = this.itiInstance.getNumber();
      const isValid = this.itiInstance.isValidNumber();
      this.valueChange.emit({ value, isValid });
      this.hasError.emit(!isValid);
    });

    this.intlTelInputObject.emit(this.itiInstance);
  }

  // Методы для обратной совместимости с старым API
  setNumber(number: string) {
    if (this.itiInstance) {
      this.itiInstance.setNumber(number);
    }
  }

  getNumber() {
    return this.itiInstance ? this.itiInstance.getNumber() : '';
  }

  isValidNumber() {
    return this.itiInstance ? this.itiInstance.isValidNumber() : false;
  }

  getSelectedCountryData() {
    return this.itiInstance ? this.itiInstance.getSelectedCountryData() : null;
  }

  setEnabled(enabled: boolean) {
    if (this.itiInstance) {
      this.itiInstance.setEnabled(enabled);
    }
  }

  getValidationError() {
    return this.itiInstance ? this.itiInstance.getValidationError() : 0;
  }

  destroy() {
    if (this.itiInstance) {
      this.itiInstance.destroy();
      this.itiInstance = null;
    }
  }
}
